import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useEffect, useState } from 'react';

type Portal = {
  id: string;
  name: string;
  slug: string;
};

async function errorMessageFromResponse(response: Response): Promise<string> {
  try {
    const data: unknown = await response.json();
    if (
      data &&
      typeof data === 'object' &&
      'message' in data &&
      typeof (data as { message?: unknown }).message === 'string'
    ) {
      return (data as { message: string }).message;
    }
  } catch {
    return `Request failed (${response.status})`;
  }

  return `Request failed (${response.status})`;
}

export function PortalWelcomePage() {
  const [portals, setPortals] = useState<Portal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadPortals() {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch('/portal/auth/my-portals', {
          credentials: 'include',
        });

        if (!response.ok) {
          const msg = await errorMessageFromResponse(response);
          if (!cancelled) setError(msg);
          if (!cancelled) setIsLoading(false);
          return;
        }

        const data: { portals: Portal[] } = await response.json();
        if (!cancelled) {
          setPortals(data.portals);

          if (data.portals.length === 1) {
            window.location.href = `/portal/${data.portals[0].id}/view`;
            return;
          }

          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Network error. Please try again.');
          setIsLoading(false);
        }
      }
    }

    void loadPortals();
    return () => {
      cancelled = true;
    };
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Card className="w-full max-w-xl">
          <CardHeader>
            <CardTitle>Loading Portals</CardTitle>
            <CardDescription>Please wait...</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Card className="w-full max-w-xl">
          <CardHeader>
            <CardTitle>Error</CardTitle>
            <CardDescription className="text-destructive">
              {error}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Please try logging in again at{' '}
              <a href="/portal/login" className="underline">
                /portal/login
              </a>
              .
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (portals.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Card className="w-full max-w-xl">
          <CardHeader>
            <CardTitle>No Portals Available</CardTitle>
            <CardDescription>
              You don't have access to any portals.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Please contact your administrator if you believe this is an error.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Select a Portal</CardTitle>
          <CardDescription>
            Choose which portal you'd like to access
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3">
            {portals.map((portal) => (
              <Button
                key={portal.id}
                variant="outline"
                className="h-auto p-3 justify-start"
                onClick={() => {
                  window.location.href = `/portal/${portal.id}/view`;
                }}
              >
                <div className="text-left">
                  <div className="font-medium">{portal.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {portal.slug}
                  </div>
                </div>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
